import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  Brain,
  TrendingUp,
  Lightbulb,
  Target,
  BookOpen,
  Sparkles,
  RefreshCw,
  Calendar,
  Award,
  Zap,
  Star,
  ChevronRight,
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { insightsService } from "@/services/insightsService";
import { format } from "date-fns";
import { toast } from "sonner";

interface GrowthInsight {
  type: "learning" | "productivity" | "wellness" | "achievement";
  title: string;
  description: string;
  impact: "high" | "medium" | "low";
  timeframe: string;
  actionable?: string;
  progress?: number;
}

interface LearningMilestone {
  date: string;
  title: string;
  description: string;
  category: string;
  confidence: number;
}

interface GrowthMetrics {
  totalDaysTracked: number;
  improvementAreas: string[];
  consistencyScore: number;
  learningMoments: number;
  growthTrajectory: "ascending" | "stable" | "variable";
}

export default function AIGrowthInsights() {
  const { user } = useUser();
  const [insights, setInsights] = useState<GrowthInsight[]>([]);
  const [milestones, setMilestones] = useState<LearningMilestone[]>([]);
  const [metrics, setMetrics] = useState<GrowthMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // ALIGNED WITH web_old: loadGrowthData with forceRefresh parameter
  // Backend endpoint: GET /insights/ai-growth?forceRefresh=false
  // MIGRATED FROM web_old/src/components/growth/AIGrowthInsights.tsx::loadGrowthData()
  const loadGrowthData = async (forceRefresh = false) => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      // ALIGNED WITH web_old: Pass forceRefresh to backend (lines 377-418)
      const response = await insightsService.getAIGrowthInsights(forceRefresh);
      
      setInsights(response?.insights || []);
      setMilestones(response?.milestones || []);
      setMetrics(response?.metrics || null);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error loading growth data:", error);
      toast.error("Failed to load growth insights");
      // Set fallback data (ALIGNED WITH web_old fallback behavior)
      setInsights([{
        type: "learning",
        title: "Start Your Growth Journey",
        description: "Begin tracking your daily progress to unlock AI-powered insights.",
        impact: "high",
        timeframe: "ongoing",
        actionable: "Complete check-ins to build your growth profile.",
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGrowthData();
  }, [user?.id]);

  const getInsightIcon = (type: string) => {
    switch (type) {
      case "learning":
        return <BookOpen className="h-4 w-4" />;
      case "productivity":
        return <Zap className="h-4 w-4" />;
      case "wellness":
        return <Sparkles className="h-4 w-4" />;
      case "achievement":
        return <Award className="h-4 w-4" />;
      default:
        return <Lightbulb className="h-4 w-4" />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case "learning":
        return "text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400";
      case "productivity":
        return "text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400";
      case "wellness":
        return "text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400";
      case "achievement":
        return "text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400";
      default:
        return "text-gray-600 bg-gray-50 dark:bg-gray-900/20 dark:text-gray-400";
    }
  };

  const getImpactBadge = (impact: string) => {
    const variants = {
      high: "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300",
      medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300",
      low: "bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300",
    };
    return variants[impact as keyof typeof variants] || variants.medium;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-48">
          <div className="text-center space-y-4">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="text-muted-foreground">Analyzing your growth journey...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
            <Brain className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">AI Growth Insights</h3>
            <p className="text-sm text-muted-foreground">
              {lastRefresh && `Last updated ${format(lastRefresh, "MMM d, h:mm a")}`}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadGrowthData(true)}  // ALIGNED WITH web_old: Pass true to force refresh (line 494)
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Growth Metrics Overview */}
      {metrics && (
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium">Days Tracked</p>
                  <p className="text-2xl font-bold text-blue-600">{metrics.totalDaysTracked}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium">Consistency</p>
                  <p className="text-2xl font-bold text-green-600">{metrics.consistencyScore}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Star className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="text-sm font-medium">Learning Moments</p>
                  <p className="text-2xl font-bold text-purple-600">{metrics.learningMoments}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Growth Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5" />
            Your Growth Insights
          </CardTitle>
          <CardDescription>
            AI-powered analysis of your learning and development patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {insights.map((insight, index) => (
              <div key={index} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-full ${getInsightColor(insight.type)}`}>
                    {getInsightIcon(insight.type)}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start justify-between">
                      <h4 className="font-medium text-sm">{insight.title}</h4>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${getImpactBadge(insight.impact)}`}>
                          {insight.impact} impact
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {insight.timeframe}
                        </Badge>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      {insight.description}
                    </p>
                    
                    {insight.progress && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{insight.progress}%</span>
                        </div>
                        <Progress value={insight.progress} className="h-2" />
                      </div>
                    )}
                    
                    {insight.actionable && (
                      <div className="flex items-center gap-2 text-sm">
                        <Target className="h-3 w-3 text-primary" />
                        <span className="text-primary font-medium">{insight.actionable}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Learning Milestones */}
      {milestones.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Recent Learning Milestones
            </CardTitle>
            <CardDescription>
              Key moments of growth and self-discovery from your reflections
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-64">
              <div className="space-y-3">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                    <div className="p-2 bg-primary/10 text-primary rounded-full">
                      <Star className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-sm">{milestone.title}</h4>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {milestone.category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(milestone.date), "MMM d")}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {milestone.description}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}